<template>
  <v-app id="inspire">
    <v-navigation-drawer
      v-if="!hideNavigation"
      v-model="drawer"
      class="app-menu"
      app
      width="330px"
    >
      <AppMenu @logout="logout" />
    </v-navigation-drawer>

    <v-app-bar
      :class="{ hideLayout: hiddenLayout }"
      app
      color="#81D3DF"
      height="64px"
    >
      <AppLogoButton v-if="hideNavigation" />

      <v-btn
        v-else
        icon
        fab
        class="v-step-3"
        color="#81D3DF"
        @click.stop="drawer = !drawer"
      >
        <v-icon>$sl-hamburger</v-icon>
      </v-btn>

      <v-spacer></v-spacer>

      <h6
        class="ma-0"
        style="
          position: absolute;
          left: 50%;
          transform: translateX(-50%);
        "
      >
        Mina Sidor
      </h6>

      <v-spacer></v-spacer>

      <div v-if="hideNavigation" style="width: 100px;"></div>

      <div class="d-flex align-center">
        <v-dialog
          v-if="!hideNavigation"
          v-model="showNotificationDialog"
          content-class="notification-dialog"
          hide-overlay
          scrollable
        >
          <template v-slot:activator="{ on }">
            <v-btn
              icon
              fab
              class="mr-2"
              v-on="on"
              @click="checkNotifications"
            >
              <v-badge
                v-if="isLoggedIn"
                overlap
                color="red"
                :value="unreadNotificationCount"
              >
                <template v-slot:badge>
                  {{ unreadNotificationCount }}
                </template>

                <v-icon>$sl-alarm</v-icon>
              </v-badge>
            </v-btn>
          </template>

          <NotificationDialog
            @close="showNotificationDialog = false"
            @readNotes="readNotifications"
            @showNotes="openNotificationPage"
            @showSettings="openSettings"
          />
        </v-dialog>

        <TopBarLocaleChanger />
      </div>
    </v-app-bar>

    <v-main>
      <router-view :key="$route.fullPath" />
    </v-main>

    <!-- ModalBase stays here -->
    <ModalBase
      v-if="!hideNavigation"
      :show="showModal"
      maxWidth="400px"
      :title="$t('menu.logout-title2')"
      submitIcon="$sl-login"
      submitBtnType="action"
      :submitText="$t('menu.login-btn')"
      :showClose="false"
      @submit="closeLogoutModal"
    >
      <template v-slot:text>
        {{ $t('menu.logout-text2') }}
      </template>
    </ModalBase>

    <CookieConsentDialog v-model="consentDialogOpen" />
  </v-app>
</template>

<script>
import API from '@/services/api'
import { mapGetters, mapActions } from 'vuex'
import AppMenu from '@/components/Common/AppMenu.vue'
import { NotificationMixin } from '@/components/mixins'
import NotificationDialog from '@/components/Notifications/NotificationDialog'
import ModalBase from '@/components/Common/ModalBase'
import ModalBaseMixin from '@/components/mixins/ModalBaseMixin'
import TopBarLocaleChanger from '../components/Common/Login/TopBarLocaleChanger.vue'
import AppLogoButton from '../components/Common/AppLogoButton.vue'
import CookieConsentDialog from '../components/CookieConsentDialog.vue'
import { hasConsentChoice, getConsent, loadConsentScript } from '@/utils/consent'

export default {
  name: 'app',

  components: {
    AppMenu,
    NotificationDialog,
    ModalBase,
    TopBarLocaleChanger,
    AppLogoButton,
    CookieConsentDialog
  },

  mixins: [NotificationMixin, ModalBaseMixin],

  data: () => ({
    interval: null,
    showNotificationDialog: false,
    drawer: null,
    dialogInstance: null,
    consentDialogOpen: false,
  }),

  computed: {
    ...mapGetters('app', [
      'locale',
      'isInitialized',
      'apiVersion',
    ]),

    ...mapGetters('currentuser', [
      'isLoggedIn',
      'notifications',
      'unreadNotificationCount',
    ]),

    hideNavigation() {
      return this.$route.name === 'LoginPage'
    },

    hiddenLayout() {
      return this.$route.name === 'QrValidation'
    },
  },

  watch: {
    isLoggedIn(val) {
      this.pollNotifications(val)
    },
  },

  created() {
    console.log('App - created')

    this.pollNotifications(this.isLoggedIn)
  },

  mounted() {
    this.$events.listen('showError', eventData =>
      this.showError(eventData)
    )

    this.$events.listen('showInfo', eventData =>
      this.showInfo(eventData)
    )

    this.$events.listen('showWarning', eventData =>
      this.showWarning(eventData)
    )

    this.$events.listen('showSuccess', eventData =>
      this.showSuccess(eventData)
    )

    this.$events.listen('showAbout', eventData =>
      this.showAbout(eventData)
    )

    this.$events.listen('loggedOut', () =>
      this.logout(true)
    )

    this.$events.listen('readNote', eventData =>
      this.readNotifications(eventData)
    )

    if (!hasConsentChoice()) {
      this.consentDialogOpen = true
    } else if (getConsent() === 'accepted') {
      loadConsentScript()
    }
  },

  methods: {
    ...mapActions('currentuser', [
      'setCurrentUser',
      'setNotifications',
    ]),

    readNotifications(noteId) {
      const data = noteId
        ? { id: Number(noteId) }
        : {}

      if (this.isLoggedIn) {
        API.readNotifications({ data })
          .then(() => {
            this.loadNotifications()
          })
          .catch(error => {
            console.log('There was an error:', error)
          })
      }
    },

    openNotificationPage() {
      this.showNotificationDialog = false

      if (
        this.$route &&
        this.$route.name !== 'Notifications'
      ) {
        this.$nextTick(() => {
          this.$router.push({
            name: 'Notifications',
            params: {
              loadTime: Date.now(),
            },
          })
        })
      }
    },

    openSettings() {
      this.showNotificationDialog = false

      if (
        this.$route &&
        this.$route.name !== 'PersonSettings'
      ) {
        this.$nextTick(() => {
          this.$router.push({
            name: 'PersonSettings',
          })
        })
      }
    },

    checkNotifications() {
      if (this.notifications.length === 0) {
        this.loadNotifications()
      }
    },

    loadNotifications() {
      if (this.isLoggedIn && this.isInitialized) {
        API.listNotifications({
          startAt: 0,
          pageSize: 100,
          sortBy: 'sentTime',
          sortDesc: true,
          search: '',
        })
          .then(response => {
            this.setNotifications(response.data.rows)
          })
          .catch(error => {
            console.log('There was an error:', error)
          })
      }
    },

    pollNotifications(start) {
      if (
        start &&
        process.env.VUE_APP_NOTIFICATION_POLL_INTERVAL
      ) {
        this.interval = setInterval(
          function () {
            if (this.isLoggedIn && this.isInitialized) {
              this.loadNotifications()
            }
          }.bind(this),
          process.env.VUE_APP_NOTIFICATION_POLL_INTERVAL
        )

        return
      }

      if (this.interval) {
        this.stopNotificationsPoll()
      }
    },

    stopNotificationsPoll() {
      clearInterval(this.interval)
    },

    // App messages
    showError(message) {
      if (typeof message === 'object') {
        const {
          message: text,
          timeout = 5000,
        } = message

        this.$dialog.message.error(text, {
          position: 'top-right',
          timeout,
        })

        return
      }

      this.$dialog.message.error(message, {
        position: 'top-right',
        timeout: 5000,
      })
    },

    showInfo(message) {
      if (typeof message === 'object') {
        const {
          message: text,
          timeout = 5000,
        } = message

        this.$dialog.message.info(text, {
          position: 'top-right',
          timeout,
        })

        return
      }

      this.$dialog.message.info(message, {
        position: 'top-right',
        timeout: 5000,
      })
    },

    showWarning(message) {
      if (typeof message === 'object') {
        const {
          message: text,
          timeout = 5000,
        } = message

        return this.$dialog.message.warning(text, {
          position: 'top-right',
          timeout,
        })
      }

      this.$dialog.message.warning(message, {
        position: 'top-right',
        timeout: 5000,
      })
    },

    showSuccess(message) {
      if (typeof message === 'object') {
        const {
          message: text,
          timeout = 5000,
        } = message

        return this.$dialog.message.success(text, {
          position: 'top-right',
          timeout,
        })
      }

      this.$dialog.message.success(message, {
        position: 'top-right',
        timeout: 5000,
      })
    },

    // Version
    showAbout() {
      this.$dialog.message.info(
        `MinaSidor ${process.env.VUE_APP_VERSION}<br/><br/>Git: ${process.env.VUE_APP_GIT_RAW}<br/>API version: ${this.apiVersion}`,
        {
          position: 'top-right',
          timeout: 5000,
        }
      )
    },

    async logout(notify) {
      this.drawer = false

      this.setCurrentUser(null)

      this.stopNotificationsPoll()

      this.setNotifications([])

      // User has just logged out, we reset the User ID (MATOMO)
      /* window._paq.push(['resetUserId']) */

      if (this.$route.path != '/') {
        this.$router.push('/').catch(() => {})
      }

      // Show notification dialog only once
      if (notify && this.dialogInstance === null) {
        this.showModal = true
        this.dialogInstance = null
      }
    },

    closeLogoutModal() {
      this.showModal = false
      this.dialogInstance = null
    },
  },
}
</script>

<style lang="scss" scoped>
@media (max-width: 768px) {
  .logo {
    display: none;
  }

  h4 {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    margin: unset !important;
  }
}
</style>